import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FirebaseSystemDrawingService, FirebaseWarehouseService, Warehouse, WarehouseObservableService } from '@damofire/data-watson';
import { WarehouseCanvasDecorator } from 'apps/damofire/src/app/watson/layout/warehouse-canvas.decorator';
import { combineLatest, filter, Subject, Subscription } from 'rxjs';
import { AutoUnsubscribe } from '../auto-unsubscribe.decorator';
import { LoaderWrapperComponent } from '../loader/loader-wrapper.component';
import { ResizeObserverDirective } from '../resize/resize-observer.directive';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@AutoUnsubscribe()
@Component({
  selector: 'app-layout-preview',
  templateUrl: './layout-preview.component.html',
  styleUrls: ['./layout-preview.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, MatProgressSpinnerModule, LoaderWrapperComponent, MatIcon, ResizeObserverDirective, MatButton],
})
export class LayoutPreviewComponent implements OnInit {
  private decorator: WarehouseCanvasDecorator;
  private canvasRef: ElementRef<HTMLCanvasElement>;

  loadingDrawing = false;
  warehouseId: string;

  private warehouseSub: Subscription;
  private dataSubs: Subscription;
  private waitForDecorator: Subject<boolean> = new Subject<boolean>();
  private warehouseChanged: Subject<Warehouse> = new Subject<Warehouse>();

  @Input()
  routerLink: string;

  @Input()
  set warehouse(id: string) {
    this.warehouseId = id;
    this.loadingDrawing = true;

    this.warehouseSub?.unsubscribe();
    this.warehouseSub = this.warehouseObsService.observeWarehouse(id).subscribe((warehouse) => this.warehouseChanged.next(warehouse));
  }

  @ViewChild('canvas')
  set canvas(canvasRef: ElementRef<HTMLCanvasElement>) {
    if (!canvasRef) {
      return;
    }

    if (this.canvasRef !== canvasRef) {
      this.decorator?.destroy();
      this.decorator = new WarehouseCanvasDecorator(canvasRef.nativeElement, false);
      this.waitForDecorator.next(true);

      this.canvasRef = canvasRef;
    }
  }

  constructor(
    private readonly warehouseService: FirebaseWarehouseService,
    private readonly warehouseObsService: WarehouseObservableService,
    private readonly systemDrawingService: FirebaseSystemDrawingService,
  ) {}

  ngOnInit(): void {
    this.dataSubs = combineLatest([this.warehouseChanged, this.waitForDecorator])
      .pipe(filter(([warehouse]) => !warehouse.import || warehouse.import.status === 'COMPLETED'))
      .subscribe(([warehouse]: [warehouse: Warehouse, decoratorIn: boolean]) => this.resolveLayoutComponents(warehouse._id));
  }

  canvasResized(width: number, height: number): void {
    if (height <= 400) {
      this.decorator?.resize(width, height);
      this.decorator?.resetZoom(true);
    }
  }

  private resolveLayoutComponents(divisionId: string): void {
    Promise.all([this.warehouseService.resolveOutline(divisionId), this.systemDrawingService.list(divisionId)])
      .then(([segments, systems]) => {
        this.decorator.updateOutline(segments);

        if (systems) {
          this.decorator.updateSystems(systems, false).catch(console.error);
        }

        this.decorator.resetZoom(true);
      })
      .catch(console.error)
      .finally(() => (this.loadingDrawing = false));
  }
}
